// One touchpoint: its versions, and the editor that writes the next one.
//
// The editable half is the body; the guard renders read-only below it —
// what an admin cannot delete is shown rather than hidden. Save and activate
// are separate acts, so a draft can exist without being live, and "revert to
// code default" is activating version 0.
import { useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Link, useParams } from "react-router";
import type { LlmParams, LlmTouchpointSlug, PromptVersion } from "@gb/admin-contracts";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  useActivate,
  useCreateVersion,
  usePreview,
  useTouchpoint,
  useVersions,
} from "./prompts-api";

export function PromptEditorPage() {
  const { slug = "" } = useParams();
  const touchpoint = useTouchpoint(slug as LlmTouchpointSlug);
  const versions = useVersions(slug as LlmTouchpointSlug);
  const create = useCreateVersion(slug as LlmTouchpointSlug);
  const activate = useActivate(slug as LlmTouchpointSlug);
  const preview = usePreview(slug as LlmTouchpointSlug);

  const [body, setBody] = useState("");
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("");
  const [maxTokens, setMaxTokens] = useState("");
  const [temperature, setTemperature] = useState("");
  const [note, setNote] = useState("");
  const [seeded, setSeeded] = useState(false);

  // Seed the editor from whichever version is live, once it arrives.
  useEffect(() => {
    if (seeded || !versions.data) return;
    // The version that runs: one switched on but out of date does not.
    const live = versions.data.find((v) => v.isActive && !v.outOfDate) ?? versions.data.at(-1);
    if (live) {
      setBody(live.body);
      setModel(live.params.model ?? "");
      setEffort(live.params.effort ?? "");
      setMaxTokens(live.params.maxTokens?.toString() ?? "");
      setTemperature(live.params.temperature?.toString() ?? "");
      setSeeded(true);
    }
  }, [seeded, versions.data]);

  if (touchpoint.isError) return <EmptyState icon={SlidersHorizontal} title="Unknown touchpoint" />;
  if (!touchpoint.data) return null;
  const t = touchpoint.data;

  const params: LlmParams = {
    ...(model.trim() ? { model: model.trim() } : {}),
    ...(effort.trim() ? { effort: effort.trim() as LlmParams["effort"] } : {}),
    ...(maxTokens.trim() ? { maxTokens: Number(maxTokens) } : {}),
    ...(temperature.trim() ? { temperature: Number(temperature) } : {}),
  };

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link to="/prompts" className="text-brand hover:underline">
          ← Prompts
        </Link>
      </div>
      <PageHeader eyebrow={t.area} title={t.name} />
      <p className="mt-1 max-w-[720px] text-[0.8rem] text-muted">{t.description}</p>
      <div className="mt-1 font-mono text-[0.72rem] text-faint">
        {t.slug} · {t.provider} · live v{t.activeVersion}
        {t.outOfDateVersion !== null ? ` (v${t.outOfDateVersion} is switched on but out of date)` : ""}{" "}
        · effective model {t.effectiveModel}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div>
          <label className="text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
            System prompt body
          </label>
          {Object.keys(t.variables).length > 0 && (
            <p className="mt-1 text-[0.72rem] text-faint">
              Placeholders this touchpoint supplies:{" "}
              {Object.entries(t.variables).map(([name, v]) => (
                <span key={name} className="mr-2 font-mono">
                  {`{{${name}}}`}
                  {v.required ? "*" : ""}
                </span>
              ))}
            </p>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="mt-2 min-h-[360px] font-mono text-[0.78rem] leading-relaxed"
          />

          <div className="mt-3 rounded-md border border-border-inner bg-canvas p-3">
            <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">
              Guard (code-owned, always appended)
            </div>
            <p className="mt-1 font-mono text-[0.72rem] leading-relaxed whitespace-pre-wrap text-faint">
              {t.guard || "(none)"}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={() =>
                void create
                  .mutateAsync({ body, params, note: note.trim() || undefined })
                  .catch(() => undefined)
              }
              disabled={create.isPending || !body.trim()}
            >
              Save as new version
            </Button>
            <Button
              variant="outline"
              onClick={() => void preview.mutateAsync({ body, params }).catch(() => undefined)}
              disabled={preview.isPending || !body.trim()}
            >
              Preview render
            </Button>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this version exists…"
              className="max-w-[320px]"
            />
          </div>
          {create.isError && (
            <p className="mt-2 text-[0.78rem] text-danger">{String(create.error)}</p>
          )}
          {preview.data && (
            <div className="mt-4 rounded-md border border-border bg-white p-3">
              <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">
                Rendered with example variables
              </div>
              {preview.data.issues.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-[0.75rem] text-danger">
                  {preview.data.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              )}
              <pre className="mt-2 overflow-x-auto font-mono text-[0.72rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
                {preview.data.rendered}
              </pre>
            </div>
          )}
        </div>

        <div>
          <div className="text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
            Params for the next version
          </div>
          <div className="mt-2 space-y-2">
            <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder={`model (default: ${t.effectiveModel})`} />
            <Input value={effort} onChange={(e) => setEffort(e.target.value)} placeholder="effort: low | medium | high | xhigh | max" />
            <Input value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="maxTokens" inputMode="numeric" />
            {t.capabilities.temperature && (
              <Input value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="temperature (0–1)" inputMode="decimal" />
            )}
          </div>

          <div className="mt-6 text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
            Versions
          </div>
          <div className="mt-2 space-y-2">
            {(versions.data ?? []).map((version) => (
              <VersionRow
                key={version.id ?? "code"}
                version={version}
                onActivate={() => void activate.mutateAsync(version.id).catch(() => undefined)}
                onLoad={() => {
                  setBody(version.body);
                  setModel(version.params.model ?? "");
                  setEffort(version.params.effort ?? "");
                  setMaxTokens(version.params.maxTokens?.toString() ?? "");
                  setTemperature(version.params.temperature?.toString() ?? "");
                }}
                busy={activate.isPending}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VersionRow({
  version,
  onActivate,
  onLoad,
  busy,
}: {
  version: PromptVersion;
  onActivate: () => void;
  onLoad: () => void;
  busy: boolean;
}) {
  const live = version.isActive && !version.outOfDate;
  return (
    <div
      className={cn(
        "rounded-md border p-3",
        live ? "border-brand bg-brand-tint-3" : "border-border-inner bg-white",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[0.75rem] font-semibold text-ink">
          v{version.version}
          {version.version === 0 ? " (code)" : ""}
        </span>
        {live ? (
          <span className="font-display text-[0.6rem] font-semibold tracking-[0.08em] text-brand uppercase">
            Live
          </span>
        ) : version.outOfDate ? (
          <span className="font-display text-[0.6rem] font-semibold tracking-[0.08em] text-warn uppercase">
            Out of date
          </span>
        ) : (
          <Button size="sm" variant="outline" onClick={onActivate} disabled={busy}>
            Activate
          </Button>
        )}
      </div>
      <div className="mt-1 text-[0.7rem] text-faint">
        {version.createdBy}
        {version.createdAt ? ` · ${new Date(version.createdAt).toLocaleString()}` : ""}
      </div>
      {version.outOfDate && (
        <div className="mt-1 text-[0.72rem] text-warn">
          Written from an older code prompt, so it is not used. Load it into the editor and save it
          again to use it.
        </div>
      )}
      {version.note && <div className="mt-1 text-[0.72rem] text-muted">{version.note}</div>}
      {Object.keys(version.params).length > 0 && (
        <div className="mt-1 font-mono text-[0.68rem] text-faint">
          {JSON.stringify(version.params)}
        </div>
      )}
      <button
        type="button"
        onClick={onLoad}
        className="mt-1 text-[0.7rem] font-semibold text-brand hover:underline"
      >
        Load into editor
      </button>
    </div>
  );
}
